import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UploadCloud, FileText, CheckCircle2, XCircle, AlertTriangle, Loader2, Download, Trash2, Play, RefreshCw, ShieldCheck, History, Eye, ExternalLink, RotateCcw, Timer, FileSpreadsheet, Search, X, FileArchive } from 'lucide-react';
import { useExtracaoFaturas, type ProcessedFile } from './ExtracaoFaturasContext';
import type { CorrecoesPlanilha } from '../../types';
import { extrairFatura, reprocessarExtracao, getPdfUrl } from '../../src/services/extracaoApi';
import { exportExtracoesToExcel } from './Extracao/excelExport';
import { motivoRevisao } from './Extracao/planilha/faturaGridModel';
import RevisaoFaturaModal from './Extracao/RevisaoFaturaModal';
import PlanilhaFaturasModal from './Extracao/planilha/PlanilhaFaturasModal';
import { baixarPdfsZip } from './Extracao/zipFaturas';

type StatusVisual = 'todos' | 'confere' | 'revisar' | 'falhou' | 'pendente';

/** Status "de negócio" de um arquivo: junta o status de extração com o da reconciliação. */
const statusDe = (f: ProcessedFile): Exclude<StatusVisual, 'todos'> | 'processando' => {
    if (f.status === 'processing') return 'processando';
    if (f.status === 'error') return 'falhou';
    if (f.status === 'success') {
        if (f.reconciliacao?.status === 'ok') return 'confere';
        if (f.reconciliacao?.status === 'revisar') return 'revisar';
        return 'falhou';
    }
    return 'pendente';
};

const fmtDuracao = (ms: number): string => {
    const s = Math.max(0, Math.round(ms / 1000));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}min ${String(s % 60).padStart(2, '0')}s`;
    return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}min`;
};

const MAX_TENTATIVAS = 3;

const ExtracaoFaturasView: React.FC = () => {
    const { files, setFiles, isProcessing, setIsProcessing, fileInputRef, garantirLoteId, resetLote, recuperando, qtdRecuperada } = useExtracaoFaturas();
    const [isDragging, setIsDragging] = useState(false);
    const [revisaoIdx, setRevisaoIdx] = useState<number | null>(null);
    const [revisaoPdfUrl, setRevisaoPdfUrl] = useState<string | null>(null);
    const [reenvioAlvo, setReenvioAlvo] = useState<number | null>(null);
    const [filtroStatus, setFiltroStatus] = useState<StatusVisual>('todos');
    const [filtroNome, setFiltroNome] = useState('');
    const [duplicatasIgnoradas, setDuplicatasIgnoradas] = useState(0);
    const [autoRetry, setAutoRetry] = useState(true);
    const [reprocessando, setReprocessando] = useState(false);
    const [reprocessandoIdx, setReprocessandoIdx] = useState<number | null>(null);
    const [eta, setEta] = useState<{ msRestante: number; msPorItem: number; rotulo: string } | null>(null);
    const [parando, setParando] = useState(false);
    const [planilhaAberta, setPlanilhaAberta] = useState(false);
    const [zipProgresso, setZipProgresso] = useState<{ feitos: number; total: number } | null>(null);
    const cancelarRef = useRef(false); // "Parar": encerra a fila após a fatura atual (os restantes ficam pendentes)
    const reenvioInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (revisaoIdx === null) { setRevisaoPdfUrl(null); return; }
        const item = files[revisaoIdx];
        if (item?.pdf_storage_path) {
            let ativo = true;
            getPdfUrl(item.pdf_storage_path).then(url => { if (ativo) setRevisaoPdfUrl(url); });
            return () => { ativo = false; };
        }
        setRevisaoPdfUrl(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisaoIdx]);

    const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault(); setIsDragging(false);
        if (e.dataTransfer.files) addFiles(Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf'));
    };
    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) addFiles(Array.from(e.target.files).filter(f => f.type === 'application/pdf'));
    };
    const addFiles = (newFiles: File[]) => {
        const next = [...files];
        const extras: ProcessedFile[] = [];
        let ignoradas = 0;
        for (const file of newFiles) {
            // Se este nome já está marcado como "precisa reenvio" (sobrou de uma sessão
            // anterior interrompida), preenche essa vaga em vez de duplicar a linha.
            const idxPlaceholder = next.findIndex(f => f.precisaReenvio && f.fileName === file.name);
            if (idxPlaceholder !== -1) {
                next[idxPlaceholder] = { ...next[idxPlaceholder], file, status: 'pending', error_message: undefined, precisaReenvio: false, recuperado: false };
                continue;
            }
            // Falhou? Re-arma a mesma linha com os bytes novos em vez de criar outra.
            const idxErro = next.findIndex(f => f.status === 'error' && f.fileName === file.name);
            if (idxErro !== -1) {
                next[idxErro] = { ...next[idxErro], file, status: 'pending', error_message: undefined, precisaReenvio: false, recuperado: false };
                continue;
            }
            // Nome já na lista (concluído ou aguardando) = duplicata — não entra de
            // novo. Era isso que inflava o lote quando o usuário re-arrastava tudo
            // após uma queda de internet.
            if (next.some(f => f.fileName === file.name) || extras.some(f => f.fileName === file.name)) {
                ignoradas++;
                continue;
            }
            extras.push({ file, fileName: file.name, status: 'pending' });
        }
        setFiles([...next, ...extras]);
        setDuplicatasIgnoradas(ignoradas);
    };

    // Botão "Selecionar arquivo" de cada linha "Reenviar" — mais direto que
    // depender do usuário arrastar de novo pro dropzone e acertar o nome.
    const acionarReenvio = (idx: number) => { setReenvioAlvo(idx); reenvioInputRef.current?.click(); };
    const handleReenvioInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        e.target.value = '';
        if (!file || reenvioAlvo === null) return;
        setFiles(prev => prev.map((f, i) => i === reenvioAlvo
            ? { ...f, file, status: 'pending', error_message: undefined, precisaReenvio: false, recuperado: false }
            : f));
        setReenvioAlvo(null);
    };

    const openPdf = async (item: typeof files[number]) => {
        if (item.file) {
            const url = URL.createObjectURL(item.file);
            window.open(url, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } else if (item.pdf_storage_path) {
            const url = await getPdfUrl(item.pdf_storage_path);
            if (url) window.open(url, '_blank', 'noopener');
        }
    };

    const removeFile = (index: number) => { if (!isProcessing) setFiles(prev => prev.filter((_, i) => i !== index)); };
    const clearFiles = () => { if (!isProcessing) { setFiles([]); resetLote(); setDuplicatasIgnoradas(0); } };
    const clearSuccesses = () => { if (!isProcessing) setFiles(prev => prev.filter(f => f.status !== 'success')); };
    const clearErrors = () => { if (!isProcessing) setFiles(prev => prev.filter(f => f.status !== 'error')); };
    const retryErrors = () => {
        // Placeholders "precisa reenvio" não têm os bytes do PDF — não dá pra
        // reprocessar sem o usuário arrastar o arquivo de novo (ver addFiles).
        if (!isProcessing) setFiles(prev => prev.map(f => (f.status === 'error' && f.file) ? { ...f, status: 'pending', error_message: undefined } : f));
    };

    /** Extrai 1 arquivo (novo OCR) e devolve o ProcessedFile atualizado. */
    const extrairUm = async (item: ProcessedFile, loteId: string): Promise<ProcessedFile> => {
        const tentativas = (item.tentativas || 0) + 1;
        try {
            const resp = await extrairFatura(item.file!, loteId);
            if (!resp.ok) throw new Error(resp.error || 'Falha na extração.');
            if (!resp.estruturado?.cabecalho?.unidade_consumidora && !resp.estruturado?.cabecalho?.nome_cliente) {
                throw new Error('A extração retornou vazia. O PDF pode estar ilegível.');
            }
            return {
                ...item, status: 'success', tentativas, error_message: undefined,
                estruturado: resp.estruturado, reconciliacao: resp.reconciliacao || null, extracao_id: resp.extracao_id,
            };
        } catch (e: any) {
            return { ...item, status: 'error', tentativas, error_message: e?.message || 'Erro desconhecido' };
        }
    };

    /** Roda uma fila de índices com estimativa de tempo (média móvel do lote atual). */
    const rodarFila = async (
        updated: ProcessedFile[],
        indices: number[],
        rotulo: string,
        executa: (item: ProcessedFile) => Promise<ProcessedFile>,
    ) => {
        const inicio = Date.now();
        let feitos = 0;
        for (const idx of indices) {
            if (cancelarRef.current) break; // "Parar": o que não começou fica como está
            updated[idx] = { ...updated[idx], status: 'processing' };
            setFiles([...updated]);
            updated[idx] = await executa(updated[idx]);
            feitos++;
            const msPorItem = (Date.now() - inicio) / feitos;
            setEta({ msPorItem, msRestante: msPorItem * (indices.length - feitos), rotulo });
            setFiles([...updated]);
        }
    };

    // "assinatura" dos resultados — se uma rodada de retry não mudou nada, o pipeline é
    // determinístico e insistir não vai mudar: para e deixa quieto.
    const assinatura = (arr: ProcessedFile[]) => arr.map(f => `${f.status}:${f.reconciliacao?.status || ''}:${f.error_message || ''}`).join('|');

    const processFiles = async () => {
        setIsProcessing(true);
        cancelarRef.current = false;
        setParando(false);
        const loteId = garantirLoteId();
        const updated = [...files];

        const pendentes = updated.map((f, i) => (f.status !== 'success' && f.file) ? i : -1).filter(i => i !== -1);
        await rodarFila(updated, pendentes, 'Extraindo', (item) => extrairUm(item, loteId));

        // Auto-retry: até 3 tentativas por arquivo. Erros de extração são re-extraídos
        // (novo OCR — cobre falha transitória); "revisar" é reprocessado sobre o OCR já
        // salvo (sem custo, sem duplicar linha no banco). Se uma rodada inteira não mudar
        // nada, desiste — não adianta bater na mesma tecla.
        if (autoRetry) {
            for (let rodada = 2; rodada <= MAX_TENTATIVAS && !cancelarRef.current; rodada++) {
                const comErro = updated.map((f, i) => (f.status === 'error' && f.file && (f.tentativas || 1) < MAX_TENTATIVAS) ? i : -1).filter(i => i !== -1);
                const emRevisao = updated.map((f, i) => (statusDe(f) === 'revisar' && f.extracao_id && (f.tentativas || 1) < MAX_TENTATIVAS) ? i : -1).filter(i => i !== -1);
                if (comErro.length === 0 && emRevisao.length === 0) break;
                const antes = assinatura(updated);
                await rodarFila(updated, comErro, `Retentando falhas (${rodada}ª tentativa)`, (item) => extrairUm(item, loteId));
                await rodarFila(updated, emRevisao, `Reprocessando revisões (${rodada}ª tentativa)`, (item) => reprocessarUm(item));
                if (assinatura(updated) === antes) break;
            }
        }
        setEta(null);
        setParando(false);
        setIsProcessing(false);
    };

    /** Reprocessa 1 extração já salva (reaproveita o OCR do banco — sem novo custo). */
    const reprocessarUm = async (item: ProcessedFile): Promise<ProcessedFile> => {
        const tentativas = (item.tentativas || 1) + 1;
        try {
            const resp = await reprocessarExtracao(item.extracao_id!);
            if (!resp.ok) throw new Error(resp.error || 'Falha ao reprocessar.');
            return { ...item, status: 'success', tentativas, estruturado: resp.estruturado, reconciliacao: resp.reconciliacao || null };
        } catch {
            return { ...item, status: 'success', tentativas }; // mantém o resultado anterior
        }
    };

    /** Manda TODAS as faturas em "Revisar" de volta pro robô (reprocesso sem novo OCR). */
    const reprocessarRevisoes = async () => {
        if (isProcessing || reprocessando) return;
        setReprocessando(true);
        cancelarRef.current = false;
        setParando(false);
        const updated = [...files];
        const alvos = updated.map((f, i) => (statusDe(f) === 'revisar' && f.extracao_id) ? i : -1).filter(i => i !== -1);
        await rodarFila(updated, alvos, 'Reprocessando revisões', (item) => reprocessarUm(item));
        setEta(null);
        setParando(false);
        setReprocessando(false);
    };

    /** Reprocessa UMA fatura específica (botão na linha) — mesmo caminho do lote, só que 1. */
    const reprocessarLinha = async (idx: number) => {
        if (ocupado || reprocessandoIdx !== null) return;
        const alvo = files[idx];
        if (!alvo?.extracao_id) return;
        setReprocessandoIdx(idx);
        setFiles(prev => prev.map((f, i) => i === idx ? { ...f, status: 'processing' } : f));
        const atualizado = await reprocessarUm(alvo);
        setFiles(prev => prev.map((f, i) => i === idx ? atualizado : f));
        setReprocessandoIdx(null);
    };

    const pararProcessamento = () => {
        cancelarRef.current = true;
        setParando(true);
    };

    /** ZIP com os PDFs de todos os arquivos da fila (clicar de novo cancela). */
    const zipaveis = files.filter(f => f.file || f.pdf_storage_path);
    const zipCancelRef = useRef(false);
    const baixarZip = async () => {
        if (zipProgresso) { zipCancelRef.current = true; return; }
        if (zipaveis.length === 0) return;
        zipCancelRef.current = false;
        setZipProgresso({ feitos: 0, total: zipaveis.length });
        try {
            await baixarPdfsZip(
                zipaveis.map(f => ({ nome: f.fileName, file: f.file, path: f.pdf_storage_path })),
                'Extracao_Faturas_PDFs',
                (feitos, total) => setZipProgresso({ feitos, total }),
                () => zipCancelRef.current,
            );
        } finally { setZipProgresso(null); }
    };

    // Fonte da planilha memoizada: recriar o array a cada render fazia o grid ser
    // remontado e levava junto o que estava sendo editado.
    const faturasPlanilha = useMemo(
        () => files.filter(f => f.status === 'success' && f.estruturado).map(f => ({
            arquivo: f.fileName, estruturado: f.estruturado!, reconciliacao: f.reconciliacao || null,
            pdfFile: f.file, pdfPath: f.pdf_storage_path, extracaoId: f.extracao_id, correcoes: f.correcoes,
        })),
        [files],
    );
    const errosPlanilha = useMemo(
        () => files.filter(f => f.status === 'error').map(f => ({ arquivo: f.fileName, erro: f.error_message || 'Erro desconhecido' })),
        [files],
    );

    /** A planilha gravou correção manual — guarda na fila em memória para o Excel e a
     *  própria planilha continuarem mostrando o valor corrigido. */
    const aplicarCorrecoesLocais = (porExtracao: Record<string, CorrecoesPlanilha>) => {
        setFiles(prev => prev.map(f => (f.extracao_id && porExtracao[f.extracao_id] ? { ...f, correcoes: porExtracao[f.extracao_id] } : f)));
    };

    const exportExcel = async (escopo: StatusVisual = 'todos') => {
        const successes = files.filter(f => f.status === 'success' && f.estruturado && (escopo === 'todos' || statusDe(f) === escopo));
        const errs = (escopo === 'todos' || escopo === 'falhou') ? files.filter(f => f.status === 'error') : [];
        if (successes.length === 0 && errs.length === 0) return;
        await exportExtracoesToExcel(
            successes.map(f => ({ arquivo: f.fileName, estruturado: f.estruturado!, reconciliacao: f.reconciliacao || null, extracaoId: f.extracao_id, correcoes: f.correcoes })),
            errs.map(f => ({ arquivo: f.fileName, erro: f.error_message || 'Erro desconhecido' })),
        );
    };

    const totalFiles = files.length;
    const processedFiles = files.filter(f => f.status === 'success' || f.status === 'error').length;
    const progressPercent = totalFiles > 0 ? Math.round((processedFiles / totalFiles) * 100) : 0;
    const successesCount = files.filter(f => f.status === 'success').length;
    const errorsCount = files.filter(f => f.status === 'error').length;
    // Placeholders "precisa reenvio" não têm os bytes do PDF — "Tentar novamente" não tem o que refazer neles.
    const retryableErrorsCount = files.filter(f => f.status === 'error' && f.file).length;

    const contagens: Record<Exclude<StatusVisual, 'todos'>, number> = { confere: 0, revisar: 0, falhou: 0, pendente: 0 };
    for (const f of files) {
        const s = statusDe(f);
        if (s !== 'processando') contagens[s]++;
    }
    const revisaveisCount = files.filter(f => statusDe(f) === 'revisar' && f.extracao_id).length;
    const ocupado = isProcessing || reprocessando;
    // Busca por texto: nome do arquivo, cliente, UC ou CPF/CNPJ — sem acento e por
    // termos (todas as palavras precisam aparecer, em qualquer campo).
    const normaliza = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const buscaAtiva = filtroNome.trim().length > 0;
    const casaBusca = (f: ProcessedFile) => {
        if (!buscaAtiva) return true;
        const alvo = normaliza(`${f.fileName} ${f.estruturado?.cabecalho?.nome_cliente || ''} ${f.estruturado?.cabecalho?.unidade_consumidora || ''} ${f.estruturado?.cabecalho?.cpf_cnpj || ''}`);
        return normaliza(filtroNome).split(/\s+/).filter(Boolean).every(t => alvo.includes(t));
    };
    const visiveis = files.map((item, idx) => ({ item, idx }))
        .filter(({ item }) => (filtroStatus === 'todos' || statusDe(item) === filtroStatus || item.status === 'processing') && casaBusca(item));

    const FILTROS: Array<{ key: StatusVisual; rotulo: string; classe: string }> = [
        { key: 'todos', rotulo: 'Todos', classe: 'text-gray-600 border-gray-300' },
        { key: 'confere', rotulo: 'Confere', classe: 'text-emerald-700 border-emerald-300' },
        { key: 'revisar', rotulo: 'Revisar', classe: 'text-amber-700 border-amber-300' },
        { key: 'falhou', rotulo: 'Falhou', classe: 'text-red-600 border-red-300' },
        { key: 'pendente', rotulo: 'Pendente', classe: 'text-gray-500 border-gray-300' },
    ];

    const statusChip = (f: typeof files[number]) => {
        if (f.status === 'pending') return <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">Pendente</span>;
        if (f.status === 'processing') return <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />;
        if (f.precisaReenvio) return (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-sky-700 bg-sky-100 dark:bg-sky-900/30 px-2 py-1 rounded" title={f.error_message}>
                <RotateCcw className="w-3.5 h-3.5" /> Reenviar
            </span>
        );
        if (f.status === 'error') return (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-600 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded" title={f.error_message}>
                <XCircle className="w-3.5 h-3.5" /> Falhou{(f.tentativas || 0) >= MAX_TENTATIVAS ? ` ${f.tentativas}×` : ''}
            </span>
        );
        const st = f.reconciliacao?.status;
        if (st === 'ok') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-1 rounded"><CheckCircle2 className="w-3.5 h-3.5" /> Confere</span>;
        if (st === 'revisar') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded"><AlertTriangle className="w-3.5 h-3.5" /> Revisar</span>;
        return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-700 bg-red-100 px-2 py-1 rounded">Falhou</span>;
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white font-poppins">Extração de Faturas</h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Extração determinística com auditoria automática — sem IA, reproduzível.</p>
                </div>
                <Link to="/gestao/extracao/historico" className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors self-start">
                    <History className="w-4 h-4" /> Histórico
                </Link>
            </div>

            {recuperando && (
                <div className="flex items-center gap-2 bg-sky-50 dark:bg-sky-900/10 border border-sky-100 dark:border-sky-900/30 rounded-xl px-4 py-3 text-sm text-sky-700 dark:text-sky-300">
                    <Loader2 className="w-4 h-4 animate-spin" /> Recuperando uma sessão de extração anterior…
                </div>
            )}
            {!recuperando && qtdRecuperada > 0 && (
                <div className="flex items-center gap-2 bg-sky-50 dark:bg-sky-900/10 border border-sky-100 dark:border-sky-900/30 rounded-xl px-4 py-3 text-sm text-sky-700 dark:text-sky-300">
                    <RotateCcw className="w-4 h-4 shrink-0" />
                    <span>Recuperamos {qtdRecuperada} fatura(s) já processada(s) de uma sessão anterior (o servidor caiu ou a página recarregou). Os arquivos marcados como <strong>"Reenviar"</strong> abaixo não terminaram a tempo — arraste-os de novo pra completar o lote.</span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                        <div
                            className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${isDragging ? 'border-emerald-500 bg-primary/5' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/80'}`}
                            onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <UploadCloud className="w-12 h-12 text-emerald-600 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Clique ou arraste os PDFs aqui</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Faturas da Equatorial em PDF (apenas .pdf)</p>
                            <input type="file" multiple accept=".pdf" className="hidden" ref={fileInputRef} onChange={handleFileInput} disabled={isProcessing} />
                        </div>
                        <input type="file" accept=".pdf" className="hidden" ref={reenvioInputRef} onChange={handleReenvioInput} />

                        {duplicatasIgnoradas > 0 && (
                            <div className="mt-3 flex items-center gap-2 text-[12px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 rounded-lg px-3 py-2">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                <span><strong>{duplicatasIgnoradas} arquivo(s) ignorado(s)</strong> — já estavam na lista com o mesmo nome (duplicata não entra de novo).</span>
                            </div>
                        )}

                        {files.length > 0 && (
                            <div className="mt-6">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                                    <h4 className="font-medium text-gray-900 dark:text-white">Arquivos ({filtroStatus === 'todos' && !buscaAtiva ? files.length : `${visiveis.length} de ${files.length}`})</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {successesCount > 0 && <button onClick={clearSuccesses} disabled={ocupado} className="text-[11px] sm:text-sm font-medium border border-green-600/30 bg-green-50 px-2 py-1 rounded-md text-green-600 hover:bg-green-100">Limpar concluídos</button>}
                                        {errorsCount > 0 && <button onClick={clearErrors} disabled={ocupado} className="text-[11px] sm:text-sm font-medium border border-orange-500/30 bg-orange-50 px-2 py-1 rounded-md text-orange-500 hover:bg-orange-100">Limpar erros</button>}
                                        <button onClick={clearFiles} disabled={ocupado} className="text-[11px] sm:text-sm font-medium border border-red-500/30 bg-red-50 px-2 py-1 rounded-md text-red-500 hover:bg-red-100">Limpar todos</button>
                                    </div>
                                </div>
                                {/* Busca por texto: acha a fatura pelo nome do cliente, arquivo, UC ou CPF/CNPJ */}
                                <div className="relative mb-3">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        type="text"
                                        value={filtroNome}
                                        onChange={e => setFiltroNome(e.target.value)}
                                        placeholder="Filtrar por nome, arquivo, UC ou CPF/CNPJ…"
                                        className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900/50 text-gray-700 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400"
                                    />
                                    {buscaAtiva && (
                                        <button onClick={() => setFiltroNome('')} title="Limpar busca" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                {/* Filtro por status — "controle total": veja só o que falhou ou o que pede revisão */}
                                <div className="flex flex-wrap gap-1.5 mb-3">
                                    {FILTROS.map(({ key, rotulo, classe }) => {
                                        const n = key === 'todos' ? files.length : contagens[key];
                                        if (key !== 'todos' && n === 0) return null;
                                        const ativo = filtroStatus === key;
                                        return (
                                            <button
                                                key={key}
                                                onClick={() => setFiltroStatus(key)}
                                                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${ativo ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 border-gray-900 dark:border-gray-100' : `bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 ${classe}`}`}
                                            >
                                                {rotulo} ({n})
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="max-h-72 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                                    {visiveis.length === 0 && (
                                        <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-400 dark:text-gray-500">
                                            <Search className="w-4 h-4" />
                                            Nenhum arquivo corresponde {buscaAtiva ? <>à busca “<span className="font-medium text-gray-500 dark:text-gray-400">{filtroNome.trim()}</span>”</> : 'ao filtro'}.
                                        </div>
                                    )}
                                    {visiveis.map(({ item, idx }) => (
                                        <div key={idx} className="flex items-center justify-between p-3 gap-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-100 dark:border-gray-700">
                                            <div className="flex items-center gap-3 overflow-hidden min-w-0">
                                                <FileText className="w-5 h-5 text-gray-400 shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm text-gray-700 dark:text-gray-300 truncate" title={item.fileName}>{item.fileName}{item.recuperado && <span className="ml-1.5 text-[10px] text-sky-600 font-medium">(recuperado)</span>}</p>
                                                    {item.status === 'success' && item.estruturado && (
                                                        <p className="text-[11px] text-gray-400 truncate">
                                                            {item.estruturado.cabecalho.nome_cliente || '—'} · R$ {item.estruturado.cabecalho.total_pagar || '—'} · {item.estruturado.itens.length} itens
                                                        </p>
                                                    )}
                                                    {item.status === 'error' && <p className="text-[11px] text-red-500 truncate" title={item.error_message}>{item.error_message}</p>}
                                                    {/* Motivo da revisão inline — poupa abrir o modal só pra saber o que não fechou */}
                                                    {item.status === 'success' && item.reconciliacao?.status !== 'ok' && (
                                                        <p className="text-[11px] text-amber-700 dark:text-amber-400 line-clamp-2" title={motivoRevisao(item.reconciliacao || null)}>
                                                            {motivoRevisao(item.reconciliacao || null)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {statusChip(item)}
                                                {(item.file || item.pdf_storage_path) && (
                                                    <button onClick={() => openPdf(item)} title="Abrir PDF em nova aba" className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:text-emerald-700 border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700">
                                                        <ExternalLink className="w-3.5 h-3.5" /> PDF
                                                    </button>
                                                )}
                                                {item.status === 'success' && (
                                                    <button onClick={() => setRevisaoIdx(idx)} className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-800 border border-emerald-200 rounded-md px-2 py-1 hover:bg-emerald-50">
                                                        <Eye className="w-3.5 h-3.5" /> Revisar
                                                    </button>
                                                )}
                                                {statusDe(item) === 'revisar' && item.extracao_id && !ocupado && (
                                                    <button
                                                        onClick={() => reprocessarLinha(idx)}
                                                        disabled={reprocessandoIdx !== null}
                                                        title="Reprocessar só esta fatura (reaproveita o OCR salvo — sem novo custo)"
                                                        className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-800 border border-amber-300 rounded-md px-2 py-1 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
                                                    >
                                                        <RefreshCw className="w-3.5 h-3.5" /> Reprocessar
                                                    </button>
                                                )}
                                                {item.precisaReenvio && !isProcessing && (
                                                    <button onClick={() => acionarReenvio(idx)} className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-700 hover:text-sky-800 border border-sky-200 rounded-md px-2 py-1 hover:bg-sky-50">
                                                        <RotateCcw className="w-3.5 h-3.5" /> Selecionar arquivo
                                                    </button>
                                                )}
                                                {(item.status === 'pending' || item.precisaReenvio) && !isProcessing && (
                                                    <button onClick={() => removeFile(idx)} className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-gray-200"><Trash2 className="w-4 h-4" /></button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 space-y-4">
                        <button onClick={processFiles} disabled={ocupado || files.length === 0 || files.every(f => f.status === 'success')} className="w-full py-3 px-4 bg-primary hover:bg-primary/80 disabled:opacity-60 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2">
                            {isProcessing ? <><Loader2 className="w-5 h-5 animate-spin" />Processando ({processedFiles}/{totalFiles})</> : <><Play className="w-5 h-5 fill-current" />Iniciar Extração</>}
                        </button>

                        {ocupado && (
                            <button onClick={pararProcessamento} disabled={parando} className="w-full py-2.5 px-4 border-2 border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-60 font-medium rounded-xl flex items-center justify-center gap-2">
                                <XCircle className="w-5 h-5" />
                                {parando ? 'Parando após a fatura atual…' : 'Parar'}
                            </button>
                        )}

                        {/* Auto-retry: cada arquivo tem até 3 tentativas; se as 3 não resolverem, fica marcado e paramos de insistir. */}
                        <label className="flex items-start gap-2.5 cursor-pointer select-none px-1">
                            <input type="checkbox" checked={autoRetry} onChange={e => setAutoRetry(e.target.checked)} disabled={ocupado} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                            <span className="text-[12px] text-gray-600 dark:text-gray-300 leading-snug">
                                <strong>Corrigir automaticamente</strong> — retenta falhas e reprocessa revisões até {MAX_TENTATIVAS}×; se não resolver, deixa marcado e para de insistir.
                            </span>
                        </label>

                        {retryableErrorsCount > 0 && !ocupado && (
                            <button onClick={retryErrors} className="w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl flex items-center justify-center gap-2"><RefreshCw className="w-5 h-5" />Tentar novamente ({retryableErrorsCount} erro(s))</button>
                        )}

                        {revisaveisCount > 0 && !isProcessing && (
                            <button onClick={reprocessarRevisoes} disabled={reprocessando} title="Roda o robô de novo sobre o OCR já salvo no banco — não relê o PDF, por isso é rápido (~0,5s/fatura) e não custa nada" className="w-full py-3 px-4 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-medium rounded-xl flex items-center justify-center gap-2">
                                {reprocessando ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
                                Reprocessar revisões ({revisaveisCount})
                            </button>
                        )}

                        {successesCount > 0 && (
                            <button onClick={() => setPlanilhaAberta(true)} className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium rounded-xl flex items-center justify-center gap-2 shadow-sm">
                                <FileSpreadsheet className="w-5 h-5" />Ver planilha na tela
                            </button>
                        )}

                        {zipaveis.length > 0 && (
                            <button
                                onClick={baixarZip}
                                title={zipProgresso ? 'Cancelar o download do ZIP' : 'Baixar os PDFs originais de todos os arquivos da fila num .zip'}
                                className={`w-full py-3 px-4 border-2 font-medium rounded-xl flex items-center justify-center gap-2 ${zipProgresso
                                    ? 'border-red-400 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                            >
                                {zipProgresso ? <><XCircle className="w-5 h-5" />Cancelar ZIP ({zipProgresso.feitos}/{zipProgresso.total})</> : <><FileArchive className="w-5 h-5" />Baixar PDFs (ZIP)</>}
                            </button>
                        )}

                        {(successesCount > 0 || errorsCount > 0) && (
                            <div className="space-y-2">
                                <button onClick={() => exportExcel('todos')} className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl flex items-center justify-center gap-2"><Download className="w-5 h-5" />Exportar Excel (tudo)</button>
                                <div className="grid grid-cols-2 gap-2">
                                    {contagens.confere > 0 && (
                                        <button onClick={() => exportExcel('confere')} className="py-2 px-3 text-[12px] font-medium rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center justify-center gap-1.5">
                                            <Download className="w-3.5 h-3.5" /> Só Confere ({contagens.confere})
                                        </button>
                                    )}
                                    {contagens.revisar > 0 && (
                                        <button onClick={() => exportExcel('revisar')} className="py-2 px-3 text-[12px] font-medium rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center justify-center gap-1.5">
                                            <Download className="w-3.5 h-3.5" /> Só Revisar ({contagens.revisar})
                                        </button>
                                    )}
                                    {contagens.falhou > 0 && (
                                        <button onClick={() => exportExcel('falhou')} className="py-2 px-3 text-[12px] font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center gap-1.5">
                                            <Download className="w-3.5 h-3.5" /> Só Falhas ({contagens.falhou})
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {totalFiles > 0 && (
                            <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-500 dark:text-gray-400">Progresso</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">{progressPercent}%</span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div className="bg-emerald-600 rounded-full h-2 transition-all duration-300" style={{ width: `${progressPercent}%` }} />
                                </div>
                                {ocupado && eta && eta.msRestante > 0 && (
                                    <div className="flex items-center gap-1.5 mt-2 text-[12px] text-gray-500 dark:text-gray-400">
                                        <Timer className="w-3.5 h-3.5 shrink-0" />
                                        <span>{eta.rotulo}: <strong className="text-gray-700 dark:text-gray-200">~{fmtDuracao(eta.msRestante)}</strong> restantes ({(eta.msPorItem / 1000).toFixed(1)}s/fatura)</span>
                                    </div>
                                )}
                                <div className="flex gap-3 mt-3 text-sm flex-wrap">
                                    <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-500" /><span className="text-gray-700 dark:text-gray-300">{contagens.confere} confere</span></span>
                                    {contagens.revisar > 0 && <span className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-500" /><span className="text-gray-700 dark:text-gray-300">{contagens.revisar} revisar</span></span>}
                                    <span className="flex items-center gap-1.5"><XCircle className="w-4 h-4 text-red-500" /><span className="text-gray-700 dark:text-gray-300">{contagens.falhou} falhas</span></span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100 dark:border-emerald-900/30 p-4 flex gap-3">
                        <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                        <p className="text-[12px] text-emerald-800 dark:text-emerald-200 leading-relaxed">
                            Cada fatura passa por <strong>reconciliação aritmética</strong> (soma dos itens = total, leitura × constante = consumo…). O que não fecha vira <strong>Revisar</strong> — clique em “Revisar” para ver os detalhes.
                        </p>
                    </div>
                </div>
            </div>

            {revisaoIdx !== null && files[revisaoIdx] && (
                <RevisaoFaturaModal
                    isOpen={revisaoIdx !== null}
                    onClose={() => setRevisaoIdx(null)}
                    arquivoNome={files[revisaoIdx].fileName}
                    estruturado={files[revisaoIdx].estruturado || null}
                    reconciliacao={files[revisaoIdx].reconciliacao || null}
                    pdfUrl={revisaoPdfUrl}
                />
            )}

            {planilhaAberta && (
                <PlanilhaFaturasModal
                    isOpen={planilhaAberta}
                    onClose={() => setPlanilhaAberta(false)}
                    titulo="Extracao_Faturas"
                    faturas={faturasPlanilha}
                    erros={errosPlanilha}
                    onCorrecoesSalvas={aplicarCorrecoesLocais}
                />
            )}
        </div>
    );
};

export default ExtracaoFaturasView;
